'use client';

import { useGameStore } from '@/store/useGameStore';
import { formatXP, getXPToNextLevel, LEVEL_THRESHOLDS } from '@/lib/gamification';

interface Props {
  compact?: boolean;
}

export function XPBar({ compact = false }: Props) {
  const xp = useGameStore((s) => s.xp);
  const level = useGameStore((s) => s.level);
  const levelName = useGameStore((s) => s.levelName);
  const progressPct = useGameStore((s) => s.getProgressToNextLevel());
  const toNext = getXPToNextLevel(xp);
  const isMaxLevel = level >= LEVEL_THRESHOLDS.length;

  if (compact) {
    return (
      <div className="flex items-center gap-2 min-w-0">
        <span
          className="flex-shrink-0 text-xs font-bold px-2 py-0.5 rounded"
          style={{ background: 'var(--color-primary)', color: '#fff' }}
        >
          Lv{level}
        </span>
        <div className="flex-1 min-w-0">
          <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.1)' }}>
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{
                width: `${progressPct}%`,
                background: 'linear-gradient(90deg, #5B4FE8, #00C9A7)',
              }}
            />
          </div>
        </div>
        <span className="flex-shrink-0 text-xs font-medium" style={{ color: 'var(--color-text-secondary)' }}>
          {formatXP(xp)} XP
        </span>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div>
          <span
            className="text-xs font-semibold px-2 py-0.5 rounded mr-2"
            style={{ background: 'rgba(91,79,232,0.2)', color: '#5B4FE8' }}
          >
            Level {level}
          </span>
          <span className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>
            {levelName}
          </span>
        </div>
        <span className="text-sm font-bold gradient-text">{formatXP(xp)} XP</span>
      </div>

      <div
        className="h-2 rounded-full overflow-hidden"
        role="progressbar"
        aria-valuenow={progressPct}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={`Level progress: ${progressPct}%`}
        style={{ background: 'rgba(255,255,255,0.1)' }}
      >
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{
            width: `${progressPct}%`,
            background: 'linear-gradient(90deg, #5B4FE8, #00C9A7)',
          }}
        />
      </div>

      {!isMaxLevel && (
        <p className="text-xs text-right" style={{ color: 'var(--color-text-muted)' }}>
          {formatXP(toNext)} XP to next level
        </p>
      )}
    </div>
  );
}
